import { useState, useEffect } from 'react';
import { Button, Card, Form, Modal, Row, Col, Badge, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSearch, faDesktop, faNetworkWired, faUser } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import parse from 'html-react-parser';
import API from '../api';
import { useAuth } from '../AuthContext';
import './Projects.css';

function hasBBCode(text) {
    return /\[(b|i|u|color|url|img|table|tr|td)\b/.test(text);
}

function parseBBCode(text) {
    return text
        .replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>')
        .replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>')
        .replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>')
        .replace(/\[color=(.*?)\](.*?)\[\/color\]/g, '<span style="color:$1">$2</span>')
        .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>')
        .replace(/\[img\](.*?)\[\/img\]/g, '<img src="$1" alt="Image" style="max-width:100%" />')
        .replace(/\[table\](.*?)\[\/table\]/gs, '<table>$1</table>')
        .replace(/\[tr\](.*?)\[\/tr\]/gs, '<tr>$1</tr>')
        .replace(/\[td\](.*?)\[\/td\]/gs, '<td>$1</td>')
        .replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');
}

function RenderDescription({ text }) {
    if (!text) return <p>No description provided.</p>;
    if (hasBBCode(text)) return <div>{parse(parseBBCode(text))}</div>;
    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
}

const STATUSES = [
    { key: 'Not Started', color: '#6c757d', label: 'Not Started' },
    { key: 'In Progress', color: '#0d6efd', label: 'In Progress' },
    { key: 'Finished', color: '#198754', label: 'Finished' },
];

const DIFFICULTIES = ['Easy', 'Medium', 'Hard', 'Insane'];

const DIFFICULTY_COLORS = {
    Easy: 'var(--color-easy)',
    Medium: 'var(--color-medium)',
    Hard: 'var(--color-hard)',
    Insane: 'var(--color-extreme)',
};

const EMPTY_PROJECT = {
    title: '', tag: 'Not Started', operatingSystem: '', createdBy: { name: '', picture: '' },
    difficulty: 'Easy', ipAddress: '', description: '',
};

function Projects() {
    const { user } = useAuth();
    const isRoot = user?.rank === 'root';
    const userInfo = {
        name: user?.username || '',
        picture: user?.profile_picture || '/default-profile.svg',
    };
    const canModify = (project) => isRoot || (!!user && project?.createdBy?.name === user.username);

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    // Data
    const [newProject, setNewProject] = useState({ ...EMPTY_PROJECT });
    const [selectedProject, setSelectedProject] = useState(null);
    const [editProject, setEditProject] = useState(null);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        if (user) setNewProject(prev => ({ ...prev, createdBy: userInfo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.username, user?.profile_picture]);

    const fetchProjects = () => {
        fetch(API.PROJECTS, { method: 'GET', credentials: 'include' })
            .then(r => r.json())
            .then(data => { setProjects(data.projects || []); setLoading(false); })
            .catch(err => { console.error(err); setLoading(false); });
    };

    const handleCreate = () => {
        fetch(API.PROJECTS_CREATE, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify(newProject),
        })
        .then(r => r.json())
        .then(data => {
            if (data.status === 'success') {
                setProjects([...projects, data.project]);
                setNewProject({ ...EMPTY_PROJECT, createdBy: userInfo });
                setShowCreateModal(false);
            }
        })
        .catch(console.error);
    };

    const handleEdit = () => {
        fetch(API.PROJECTS_UPDATE, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify(editProject),
        })
        .then(r => r.json())
        .then(data => {
            if (data.status === 'success') {
                setProjects(projects.map(p => p.id === editProject.id ? editProject : p));
                setEditProject(null);
                setShowEditModal(false);
                setShowDetailModal(false);
            }
        })
        .catch(console.error);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        fetch(API.PROJECTS_DELETE, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ id }),
        })
        .then(r => r.json())
        .then(data => {
            if (data.status === 'success') {
                setProjects(projects.filter(p => p.id !== id));
                setShowDetailModal(false);
                setShowEditModal(false);
            }
        })
        .catch(console.error);
    };

    const filteredProjects = (status) => {
        return projects
            .filter(p => p.tag === status)
            .filter(p => {
                if (!search) return true;
                const q = search.toLowerCase();
                return p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
            });
    };

    const openDetail = (project) => { setSelectedProject(project); setShowDetailModal(true); };
    const openEdit = (project) => { setEditProject({ ...project }); setShowEditModal(true); };

    if (loading) return <div className="loading-spinner"></div>;

    return (
        <Container fluid className="projects-container">
            {/* Header */}
            <div className="projects-header">
                <h2>Penetration Projects</h2>
                <div className="projects-toolbar">
                    <div className="projects-search">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <Form.Control
                            type="text"
                            placeholder="Search projects..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <Button variant="danger" onClick={() => setShowCreateModal(true)}>
                        <FontAwesomeIcon icon={faPlus} /> New Project
                    </Button>
                </div>
            </div>

            {/* Kanban Board */}
            <Row className="kanban-board">
                {STATUSES.map(({ key, color, label }) => {
                    const columnProjects = filteredProjects(key);
                    return (
                        <Col key={key} lg={4} className="kanban-column-wrapper">
                            <div className="kanban-column">
                                <div className="kanban-column-header">
                                    <span className="kanban-status-dot" style={{ backgroundColor: color }}></span>
                                    <h5>{label}</h5>
                                    <Badge bg="secondary" pill className="kanban-count">{columnProjects.length}</Badge>
                                </div>
                                <div className="kanban-cards">
                                    {columnProjects.length === 0 && (
                                        <div className="kanban-empty">No projects</div>
                                    )}
                                    {columnProjects.map(project => (
                                        <Card key={project.id} className="project-card" onClick={() => openDetail(project)}>
                                            <Card.Body>
                                                <div className="project-card-header">
                                                    <Card.Title className="project-card-title">{project.title}</Card.Title>
                                                    <Badge
                                                        className="difficulty-badge"
                                                        style={{ backgroundColor: DIFFICULTY_COLORS[project.difficulty] }}
                                                    >
                                                        {project.difficulty}
                                                    </Badge>
                                                </div>
                                                <p className="project-card-desc">
                                                    {project.description.length > 80
                                                        ? project.description.substring(0, 80) + '...'
                                                        : project.description}
                                                </p>
                                                <div className="project-card-meta">
                                                    {project.operatingSystem && (
                                                        <span className="meta-item">
                                                            <FontAwesomeIcon icon={faDesktop} /> {project.operatingSystem}
                                                        </span>
                                                    )}
                                                    {project.ipAddress && (
                                                        <span className="meta-item">
                                                            <FontAwesomeIcon icon={faNetworkWired} /> {project.ipAddress}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="project-card-footer">
                                                    <div className="project-creator">
                                                        <img src={project.createdBy?.picture || '/default-profile.svg'} alt="" className="creator-avatar" />
                                                        <span>{project.createdBy?.name}</span>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
                {selectedProject && (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {selectedProject.title}
                                <Badge className="ms-2 difficulty-badge" style={{ backgroundColor: DIFFICULTY_COLORS[selectedProject.difficulty] }}>
                                    {selectedProject.difficulty}
                                </Badge>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="detail-modal-body">
                            <Row>
                                <Col md={8}>
                                    <h6>Description</h6>
                                    <div className="detail-description">
                                        <RenderDescription text={selectedProject.description} />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-sidebar">
                                        <div className="detail-field">
                                            <label>Status</label>
                                            <Badge style={{ backgroundColor: STATUSES.find(s => s.key === selectedProject.tag)?.color }}>
                                                {selectedProject.tag}
                                            </Badge>
                                        </div>
                                        <div className="detail-field">
                                            <label><FontAwesomeIcon icon={faDesktop} /> Operating System</label>
                                            <span>{selectedProject.operatingSystem || 'N/A'}</span>
                                        </div>
                                        <div className="detail-field">
                                            <label><FontAwesomeIcon icon={faNetworkWired} /> IP Address</label>
                                            <code>{selectedProject.ipAddress || 'N/A'}</code>
                                        </div>
                                        <div className="detail-field">
                                            <label><FontAwesomeIcon icon={faUser} /> Created By</label>
                                            <div className="detail-creator">
                                                <img src={selectedProject.createdBy?.picture || '/default-profile.svg'} alt="" className="creator-avatar" />
                                                <span>{selectedProject.createdBy?.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            {canModify(selectedProject) && (() => {
                                const rootOverride = isRoot && selectedProject.createdBy?.name !== user?.username;
                                return (
                                    <>
                                        <Button
                                            variant={rootOverride ? 'dark' : 'outline-danger'}
                                            title={rootOverride ? 'Delete as root (not your project)' : 'Delete project'}
                                            onClick={() => handleDelete(selectedProject.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                        </Button>
                                        <Button
                                            variant={rootOverride ? 'dark' : 'primary'}
                                            title={rootOverride ? 'Edit as root (not your project)' : 'Edit project'}
                                            onClick={() => { openEdit(selectedProject); setShowDetailModal(false); }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                        </Button>
                                    </>
                                );
                            })()}
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            {/* Create Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>New Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProjectForm
                        data={newProject}
                        onChange={setNewProject}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleCreate} disabled={!newProject.title.trim()}>Create Project</Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editProject && <ProjectForm data={editProject} onChange={setEditProject} />}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-danger" onClick={() => { handleDelete(editProject?.id); }}>
                        <FontAwesomeIcon icon={faTrash} /> Delete
                    </Button>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleEdit}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

function ProjectForm({ data, onChange }) {
    const update = (field, value) => onChange({ ...data, [field]: value });

    return (
        <Form>
            <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control type="text" value={data.title} onChange={(e) => update('title', e.target.value)} placeholder="Project name" />
            </Form.Group>
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={data.tag} onChange={(e) => update('tag', e.target.value)}>
                            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Difficulty</Form.Label>
                        <Form.Select value={data.difficulty} onChange={(e) => update('difficulty', e.target.value)}>
                            {DIFFICULTIES.map(d => <option key={d}>{d}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Operating System</Form.Label>
                        <Form.Control type="text" value={data.operatingSystem} onChange={(e) => update('operatingSystem', e.target.value)} placeholder="e.g. Linux" />
                    </Form.Group>
                </Col>
            </Row>
            <Form.Group className="mb-3">
                <Form.Label>IP Address</Form.Label>
                <Form.Control type="text" value={data.ipAddress} onChange={(e) => update('ipAddress', e.target.value)} placeholder="e.g. 10.10.10.1" />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Description (Markdown supported)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={6}
                    value={data.description}
                    onChange={(e) => update('description', e.target.value)}
                    placeholder="Describe the project. Use **bold**, *italic*, `code`, etc."
                    className="font-monospace"
                />
            </Form.Group>
        </Form>
    );
}

export default Projects;
